// StandardMaintenanceItemSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { useInsertData } from "../../hooks/useInsertData";
import { useGetData } from "../../hooks/useGetData";
import { useUpdateData } from "../../hooks/useUpdateDate";
import useDeleteData from "../../hooks/useDeleteData";

export const createStandardMaintenanceItem = createAsyncThunk(
  "standardMaintenanceItem/createStandardMaintenanceItem",
  async (data, { rejectWithValue }) => {
    try {
      const res = await useInsertData("standardItems", data);
      return res.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue(err.message);
    }
  }
);

export const getAllStandardMaintenanceItems = createAsyncThunk(
  "standardMaintenanceItem/getAllStandardMaintenanceItems",
  async (page = 1, { rejectWithValue }) => {
    try {
      const res = await useGetData(`standardItems?page=${page}`);
      return res;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue(err.message);
    }
  }
);

// إضافة thunk للبحث - جديد (اختياري إذا كان الـ Backend يدعم البحث)
export const searchStandardMaintenanceItems = createAsyncThunk(
  "standardMaintenanceItem/searchStandardMaintenanceItems",
  async ({ keyword, page = 1 }, { rejectWithValue }) => {
    try {
      const res = await useGetData(
        `standardItems?keyword=${encodeURIComponent(keyword)}&page=${page}`
      );
      return res;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue(err.message);
    }
  }
);

export const getStandardMaintenanceItemsById = createAsyncThunk(
  "standardMaintenanceItem/getStandardMaintenanceItemsById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await useGetData(`standardItems/${id}`);
      return res.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue(err.message);
    }
  }
);

export const updateStandardMaintenanceItem = createAsyncThunk(
  "standardMaintenanceItem/updateStandardMaintenanceItem",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await useUpdateData(`standardItems/${id}`, data);
      return res.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue(err.message);
    }
  }
);

export const deleteStandardMaintenanceItem = createAsyncThunk(
  "maintenanceItem/deleteStandardMaintenanceItem",
  async (id, { rejectWithValue }) => {
    try {
      const res = await useDeleteData(`standardItems/force/${id}`);
      return res.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue(err.message);
    }
  }
);

const initialState = {
  standardMaintenanceItem: null,
  allStandardMaintenanceItems: null,
  searchResults: null, // جديد
  detailsStandardMaintenanceItemsById: null,
  status: "idle",
  searchStatus: "idle", // جديد
  error: null,
  token: null,
};

export const standardMaintenanceItemSlice = createSlice({
  name: "StandardMaintenanceItemSlice",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createStandardMaintenanceItem.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createStandardMaintenanceItem.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.standardMaintenanceItem = action.payload;
      })
      .addCase(createStandardMaintenanceItem.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // //---------------------------------------
      .addCase(getAllStandardMaintenanceItems.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getAllStandardMaintenanceItems.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.allStandardMaintenanceItems = action.payload;
      })
      .addCase(getAllStandardMaintenanceItems.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // //---------------------------------------
      // Search - جديد
      .addCase(searchStandardMaintenanceItems.pending, (state) => {
        state.searchStatus = "loading";
        state.error = null;
      })
      .addCase(searchStandardMaintenanceItems.fulfilled, (state, action) => {
        state.searchStatus = "succeeded";
        state.searchResults = action.payload;
      })
      .addCase(searchStandardMaintenanceItems.rejected, (state, action) => {
        state.searchStatus = "failed";
        state.error = action.payload;
      })
      // //---------------------------------------
      .addCase(getStandardMaintenanceItemsById.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getStandardMaintenanceItemsById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.detailsStandardMaintenanceItemsById = action.payload;
      })
      .addCase(getStandardMaintenanceItemsById.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // //---------------------------------------
      .addCase(deleteStandardMaintenanceItem.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(deleteStandardMaintenanceItem.fulfilled, (state, action) => {
        state.status = "succeeded";
      })
      .addCase(deleteStandardMaintenanceItem.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export default standardMaintenanceItemSlice.reducer;
